import React, { useState } from "react";
import CampaignAccordion from "../MetaTravelAdsFormComponents/Campaign/CampaignAccordion";
import ObjectiveAccordion from "../MetaTravelAdsFormComponents/Objective/ObjectiveAccordion";
import CampaignSettingsAccordion from "../MetaTravelAdsFormComponents/CampaignSettings/CampaignSettingsAccordion";
import { useSelector } from "react-redux";
import { ExpandMore } from "@mui/icons-material";
import RetargetingForm from "../MetaTravelAdsFormComponents/AudiencesForm/RetargetingForm";
import ProspectingForm from "../MetaTravelAdsFormComponents/AudiencesForm/ProspectingForm";
import MetaAudienceForm from "../MetaTravelAdsFormComponents/AudiencesForm/MetaAudienceForm";
import { ReactComponent as IconAIGreen } from "assets/images/IconAIGreen.svg";
import AdsCreative from "../MetaTravelAdsFormComponents/AdsCreative/AdsCreative";
import { validateMetaAdsCampaignSettings } from "../Utils/validators/campaignSettingsValidators";
import { validateAudience } from "../Utils/validators/audienceValidators";
import { initialRules } from "pages/metaTravelAdsCampaignCreator/metaTravelAdsCampaignCreator";

const MetaTravelAdsCampaignCreator = ({
  expandedPanel,
  setExpandedPanel,
  selectedObjective,
  setSelectedObjective,
  visitedPanels,
  handleNext,
  selectedCountries,
  setSelectedCountries,
  selectedLanguage,
  setSelectedLanguage,
  targetValue,
  setTargetValue,
  biddingStrategy,
  setBiddingStrategy,
  campaignName,
  setCampaignName,
  campaignBudget,
  setCampaignBudget,
  monthlyBudget,
  setMonthlyBudget,
  dailyBudgetCap,
  setDailyBudgetCap,
  placementDetails,
  setPlacementDetails,
  currencySymbol,
  ageRange,
  setAgeRange,
  selectedGender,
  setSelectedGender,
  isBookingWindowEnabled,
  setIsBookingWindowEnabled,
  bookingWindowRange,
  setBookingWindowRange,
  rules,
  setRules,
  adsCreativeCards,
  setAdsCreativeCards,
  clickThroughValue,
  setClickThroughValue,
  viewThroughValue,
  setViewThroughValue,
  selectedMetaAudience,
  setSelectedMetaAudience,
  selectedIncludeAudiences,
  setSelectedIncludeAudiences,
  selectedExcludeAudiences,
  setSelectedExcludeAudiences,
  metaAudienceOptions,
  isLoadingMetaAudienceOptions,
  isSharedAudienceEnabled,
  isMultiHotelEnabled,
  selectedHotels,
  setSelectedHotels,
}) => {
  const accountId = useSelector((state) => state?.account?.account?.account_id);
  const { hotel_id = [], hotelList = [] } = useSelector((state) => state?.account);
  const selectedHotel = hotelList.find((h) => h.hotel_id === hotel_id[0]);
  const [errors, setErrors] = useState({});
  const [allHotelsDone, setAllHotelsDone] = useState(false);

  const handleNextWithValidation = async (currentPanel) => {
    if (currentPanel === "settings") {
      if (biddingStrategy === "MAXIMIZE_CONVERSIONS" && campaignBudget !== "" && isNaN(Number(campaignBudget))) {
        setErrors({ campaignTargetBudget: "Max CPA must be a valid number" });
        return;
      }
      const result = await validateMetaAdsCampaignSettings(
        {
          campaign_name: campaignName,
          bidding_strategy: {
            bidding_strategy_option: biddingStrategy,
          },
          budget: {
            default_budget: targetValue,
            ...monthlyBudget,
            is_daily_budget: dailyBudgetCap,
          },
          placement_platform: placementDetails.placement_platform,
          placement_device: placementDetails.placement_device,
          point_of_sales: selectedCountries,
          campaign_lang: selectedLanguage?.map((lang) => lang.id_lang) || [],
          attr_click_through: Number(clickThroughValue),
          attr_view_through: Number(viewThroughValue),
        },
        accountId,
        hotel_id[0]
      );
      if(result.success) {
        handleNext(currentPanel);
        return;
      }
      setErrors(result.errors || {});
    } else if (currentPanel === "audiences") {
      // Validate Meta Audience selection
      if (selectedObjective === "META_AUDIENCE") {
        if (!selectedMetaAudience && selectedIncludeAudiences.length === 0) {
          setErrors({ metaAudience: "Please select at least one audience to include" });
          return;
        }
        setErrors({});
        handleNext(currentPanel);
        return;
      }

      const payload = {
        audience_type: selectedObjective,
        age_min: Number(ageRange.min),
        age_max: Number(ageRange.max),
        gender: selectedGender,
        booking_window_start: Number(bookingWindowRange.booking_window_start),
        booking_window_end: Number(bookingWindowRange.booking_window_end),
        rules: rules.filter((rule) => !initialRules.some(initialRule => JSON.stringify(initialRule) === JSON.stringify(rule))).map((rule) => ({
          type: rule.type,
          source: rule.source,
          operator: rule.operator,
          days_from: Number(rule.daysRange?.days_from),
          days_to: Number(rule.daysRange?.days_to),
        })),
      };

      const result = await validateAudience(payload);
      if(result.success && Object.keys(result.errors || {}).length === 0) {
        handleNext(currentPanel);
        return;
      }
      setErrors(result.errors || {});
    } else {
      handleNext(currentPanel);
    }
  };
  return (
    <>
      <CampaignAccordion
        expandIcon={<ExpandMore />}
        title={`1. Campaign Settings${selectedHotel ? ` - ${selectedHotel.hotel_name} - ${hotel_id[0]}` : ""}`}
        expanded={expandedPanel === "settings"}
        onChange={() => setExpandedPanel("settings")}
        onNext={() => handleNextWithValidation("settings")}
        disabled={!visitedPanels.includes("settings")}
        visited={visitedPanels.includes("settings")}
      >
        <CampaignSettingsAccordion
          biddingStrategy={biddingStrategy}
          setBiddingStrategy={setBiddingStrategy}
          campaignName={campaignName}
          setCampaignName={setCampaignName}
          campaignBudget={campaignBudget}
          setCampaignBudget={setCampaignBudget}
          targetValue={targetValue}
          setTargetValue={setTargetValue}
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguage}
          selectedCountries={selectedCountries}
          setSelectedCountries={setSelectedCountries}
          monthlyBudget={monthlyBudget}
          setMonthlyBudget={setMonthlyBudget}
          errors={errors}
          dailyBudgetCap={dailyBudgetCap}
          setDailyBudgetCap={setDailyBudgetCap}
          placementDetails={placementDetails}
          setPlacementDetails={setPlacementDetails}
          currencySymbol={currencySymbol}
          clickThroughValue={clickThroughValue}
          setClickThroughValue={setClickThroughValue}
          viewThroughValue={viewThroughValue}
          setViewThroughValue={setViewThroughValue}
          selectedHotels={selectedHotels}
          setSelectedHotels={setSelectedHotels}
          isMultiHotelEnabled={isMultiHotelEnabled}
          lockedHotels={hotel_id?.[0] ? [String(hotel_id[0])] : []}
        />
      </CampaignAccordion>

      <CampaignAccordion
        expandIcon={<ExpandMore />}
        title="2. Audiences"
        expanded={expandedPanel === "audiences"}
        onChange={() => setExpandedPanel("audiences")}
        onNext={() => handleNextWithValidation("audiences")}
        disabled={!visitedPanels.includes("audiences")}
        visited={visitedPanels.includes("audiences")}
      >
        <div style={{ marginBottom: "16px", paddingLeft: "40px" }}>
          <ObjectiveAccordion
            selectedObjective={selectedObjective}
            setSelectedObjective={setSelectedObjective}
            isSharedAudienceEnabled={isSharedAudienceEnabled}
          />
          {selectedObjective === "RETARGETING" && (
            <RetargetingForm
              ageRange={ageRange}
              setAgeRange={setAgeRange}
              selectedGender={selectedGender}
              setSelectedGender={setSelectedGender}
              isBookingWindowEnabled={isBookingWindowEnabled}
              setIsBookingWindowEnabled={setIsBookingWindowEnabled}
              bookingWindowRange={bookingWindowRange}
              setBookingWindowRange={setBookingWindowRange}
              rules={rules}
              setRules={setRules}
              errors={errors}
            />
          )}
          {selectedObjective === "PROSPECTING" && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "22px" }}
            >
              <p
                style={{
                  display: "flex",
                  gap: "8px",
                  fontSize: "12px",
                  fontWeight: 400,
                  color: "#373F41",
                  marginTop: "30px",
                }}
              >
                <IconAIGreen fontSize="26px" />
                <span style={{ maxWidth: "380px" }}>
                  Your Audience setup is creating more opportunities to improve
                  performance with AI. We won't reach people beyond the controls
                  that you set, even with Advantage+ on.
                </span>
              </p>
              <ProspectingForm
                ageRange={ageRange}
                setAgeRange={setAgeRange}
                selectedGender={selectedGender}
                setSelectedGender={setSelectedGender}
                isBookingWindowEnabled={isBookingWindowEnabled}
                setIsBookingWindowEnabled={setIsBookingWindowEnabled}
                bookingWindowRange={bookingWindowRange}
                setBookingWindowRange={setBookingWindowRange}
                rules={rules}
                setRules={setRules}
                errors={errors}
                selectedIncludeAudiences={selectedIncludeAudiences}
                setSelectedIncludeAudiences={setSelectedIncludeAudiences}
                metaAudienceOptions={metaAudienceOptions}
                isLoadingMetaAudienceOptions={isLoadingMetaAudienceOptions}
                isSharedAudienceEnabled={isSharedAudienceEnabled}
              />
            </div>
          )}
          {selectedObjective === "META_AUDIENCE" && isSharedAudienceEnabled && (
            <MetaAudienceForm
              selectedMetaAudience={selectedMetaAudience}
              setSelectedMetaAudience={setSelectedMetaAudience}
              selectedIncludeAudiences={selectedIncludeAudiences}
              setSelectedIncludeAudiences={setSelectedIncludeAudiences}
              selectedExcludeAudiences={selectedExcludeAudiences}
              setSelectedExcludeAudiences={setSelectedExcludeAudiences}
              metaAudienceOptions={metaAudienceOptions}
              isLoadingOptions={isLoadingMetaAudienceOptions}
              error={errors?.metaAudience || ""}
            />
          )}
        </div>
      </CampaignAccordion>

      <CampaignAccordion
        expandIcon={<ExpandMore />}
        title="3. Ads Creative"
        expanded={expandedPanel === "adsCreative"}
        onChange={() => setExpandedPanel("adsCreative")}
        onNext={() => handleNextWithValidation("adsCreative")}
        disabled={!visitedPanels.includes("adsCreative")}
        visited={visitedPanels.includes("adsCreative")}
        isCreateButton
        createButtonLabel="Publish"
        disabledNext={!allHotelsDone}
      >
        <AdsCreative hotelId={hotel_id?.[0]} adsCreativeCards={adsCreativeCards} setAdsCreativeCards={setAdsCreativeCards} selectedHotels={selectedHotels} onAllDoneChange={setAllHotelsDone} isMultiHotelEnabled={isMultiHotelEnabled} selectedLanguage={selectedLanguage} />
      </CampaignAccordion>
    </>
  );
};

export default MetaTravelAdsCampaignCreator;
